import { InvoiceDiscountType } from "../enums/InvoiceDiscountType";
import { InvoicePaymentTerms } from "../enums/InvoicePaymentTerms";
const { generateCustomFieldsValidation } = require("./customFieldValidations");

const yup = require("yup");

const invoiceDetailSchema = yup.object().shape({
  id: yup.string(),
  invoiceId: yup.string(),
  order: yup.number().required(),
  productOrService: yup.string().required("This field is required"),
  description: yup.string(),
  amount: yup
    .number()
    .required("Amount is required")
    .min(0, "Amount must be at least 0")
    .test(
      "max-2-decimals",
      "Amount cannot have more than 2 decimal places",
      (value) =>
        value === undefined || /^\d+(?:\.\d{1,2})?$/.test(String(value)),
    ),
  quantity: yup
    .number()
    .required("Quantity is required")
    .min(0, "Quantity cannot be negative"),
  taxable: yup.boolean(),
});

const sendInvoiceValidation = yup.object().shape({
  id: yup.string(),
  invoiceNumber: yup.string().required("Invoice number is required"),
  invoiceName: yup.string().required("Invoice name is required"),
  primaryRecipient: yup
    .object()
    .typeError("Primary recipient is required")
    .required("Primary recipient is required"),
  secondaryRecipients: yup
    .array()
    .of(yup.object())
    .test(
      "no-duplicate-primary-email",
      "CC recipients cannot have the same email as the primary recipient.",
      function (secondaryRecipients) {
        const { primaryRecipient } = this.parent;
        if (
          !primaryRecipient ||
          !primaryRecipient.email ||
          !Array.isArray(secondaryRecipients)
        )
          return true;
        return !secondaryRecipients.some(
          (recipient) =>
            recipient &&
            recipient.email &&
            recipient.email === primaryRecipient.email,
        );
      },
    )
    .test(
      "no-duplicate-secondary-emails",
      "CC recipients cannot have duplicate emails.",
      function (secondaryRecipients) {
        if (!Array.isArray(secondaryRecipients)) return true;
        const emails = secondaryRecipients
          .map((recipient) => recipient && recipient.email)
          .filter(Boolean);
        const uniqueEmails = new Set(emails);
        return emails.length === uniqueEmails.size;
      },
    )
    .nullable(),
  paymentTerms: yup
    .mixed()
    .oneOf(Object.values(InvoicePaymentTerms))
    .required("Payment terms are required"),
  invoiceDate: yup
    .date()
    .typeError("Invoice date must be a valid date")
    .required("Invoice date is required"),
  sentDate: yup.date().nullable(),
  dueDate: yup.date().nullable(),
  subtotal: yup.number().required(),
  taxAmount: yup.number().min(0, "Tax cannot be less than 0"),
  discountAmount: yup.number().max(0, "Discount cannot be more than 0"),
  discountType: yup
    .mixed()
    .oneOf(Object.values(InvoiceDiscountType))
    .required(),
  serviceFeeAmount: yup.number().min(0, "Service fee cannot be less than 0"),
  serviceFeeType: yup
    .mixed()
    .oneOf(Object.values(InvoiceDiscountType))
    .required(),
  total: yup.number().required().moreThan(1, "Total must be greater than 1.00"),
  currency: yup.string().required(),
  attachments: yup.array().of(
    yup.object().shape({
      assetUrl: yup.string().required(),
      id: yup.string().required(),
      extension: yup.string().required(),
      parentId: yup.string().nullable(),
    }),
  ),
  details: yup
    .array()
    .of(invoiceDetailSchema)
    .min(1, "At least one item is required"),
  status: yup.string(),
  createdAt: yup.date(),
  note: yup.string(),
  allocationType: yup
    .object()
    .shape({
      key: yup.string(),
      name: yup.string(),
    })
    .nullable(),
  allocationName: yup
    .object()
    .shape({
      id: yup.string(),
      allocationCode: yup.string(),
    })
    .nullable(),
});

const generateInvoiceValidation = (customFieldSettings = []) => {
  const customFieldsSchema =
    generateCustomFieldsValidation(customFieldSettings);
  return sendInvoiceValidation.concat(
    yup.object({
      customFields: customFieldsSchema,
    }),
  );
};

export {
  invoiceDetailSchema,
  sendInvoiceValidation,
  generateInvoiceValidation,
};
